import {Spinner} from 'quickstart/components/layout/Spinner'
import {UseSearchReturn, useVisible} from 'quickstart/hooks'
import {CSSProperties, useEffect, useMemo} from 'react'
import {logger, MetaObject} from 'tizra'

const log = logger('BrowseGallery/useInfiniteBrowse')

export type UseInfiniteBrowseProps = {
  search: UseSearchReturn<MetaObject>
  maxItems?: number
}

export const useInfiniteBrowse = ({
  maxItems = 0,
  search: {hasNextPage, fetchNextPage, isFetching, lastResults, results},
}: UseInfiniteBrowseProps) => {
  const items = useMemo(
    () =>
      lastResults && maxItems ? lastResults.slice(0, maxItems) : lastResults,
    [lastResults, maxItems],
  )

  const [bottomIsVisible, bottomRef] = useVisible({bots: false})

  useEffect(() => {
    if (
      bottomIsVisible &&
      results &&
      (!maxItems || results.length < maxItems) &&
      hasNextPage &&
      !isFetching
    ) {
      fetchNextPage!()
    }
  }, [
    bottomIsVisible,
    hasNextPage,
    fetchNextPage,
    isFetching,
    maxItems,
    results,
  ])

  // Caller can display a spinner while fetching.
  const spinner =
    isFetching &&
    (results?.length ?
      // When search.results, that means the user has scrolled and we are
      // fetching more results to append to the bottom. This is different from
      // search.lastResults which doesn't update until new results finish
      // fetching. Show a standalone spinner under the gallery.
      <div style={{height: '2rem'}}>
        <Spinner dim={false} />
      </div>
      // Otherwise, we are either in the initial fetch, or executing a new
      // search, such as when a filter changes. Overlay the spinner on the
      // existing results.
    : <Spinner />)

  const spinnerWrapperStyle: CSSProperties = {
    position: 'relative',
    // During the initial load, ensure the spinner has room to display.
    ...(isFetching && !lastResults?.length && {minHeight: '30wh'}),
  }

  log.debug?.({bottomIsVisible, isFetching, hasNextPage, items, maxItems})

  return {items, bottomRef, spinner, spinnerWrapperStyle}
}
